import { IPublicClientApplication } from '@azure/msal-browser'
import { faInfoCircle, faLocationDot } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AspectRatio,
  Badge,
  Button,
  Group,
  Image,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAllLocationsBundleSummary } from '../../data/advertisement.service'
import { AllLocationsBundleSummary } from '../../models/Dashboard/AllLocationsBundleSummary'
import { COLORS, defaultImageUrl } from '../../utils/constants'
import { getAllLocationsDurationLabel } from '../../utils/displayUtils'

interface AllLocationsBundleRowProps {
  advertisementId: number
  instance: IPublicClientApplication
}

export function AllLocationsBundleRow({
  advertisementId,
  instance,
}: AllLocationsBundleRowProps) {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<AllLocationsBundleSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const data = await fetchAllLocationsBundleSummary(
          advertisementId,
          instance,
        )
        if (!cancelled) setSummary(data)
      } catch (err) {
        if (!cancelled)
          console.error('Failed to load all-locations bundle summary', err)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [advertisementId, instance])

  if (isLoading) {
    return (
      <Paper withBorder p='md' radius='md'>
        <Group align='flex-start' gap='xl'>
          <Skeleton height={175} width={311} radius='md' />
          <Stack style={{ flex: 1 }} gap='sm'>
            <Skeleton height={28} width={260} />
            <Skeleton height={20} width={180} />
            <Skeleton height={20} width={220} />
            <Skeleton height={20} width={150} />
          </Stack>
        </Group>
      </Paper>
    )
  }

  if (!summary) return null

  const dateRange = (() => {
    const start = summary.startDateLocal
      ? new Date(summary.startDateLocal).toLocaleDateString()
      : null
    const end = summary.endDateLocal
      ? new Date(summary.endDateLocal).toLocaleDateString()
      : null
    if (start && end) return `${start} – ${end}`
    if (start) return `Starts ${start}`
    return null
  })()

  const approvalParts: string[] = []
  if (summary.approvedCount > 0)
    approvalParts.push(`${summary.approvedCount} approved`)
  if (summary.pendingCount > 0)
    approvalParts.push(`${summary.pendingCount} pending`)
  if (summary.deniedCount > 0)
    approvalParts.push(`${summary.deniedCount} denied`)

  return (
    <Paper withBorder p='md' radius='md'>
      <Group align='flex-start' gap='xl'>
        <AspectRatio ratio={16 / 9} h={175} w={311}>
          <Image
            src={summary.imageUrl || defaultImageUrl}
            radius='md'
            alt={summary.name ?? 'All Locations Bundle'}
          />
        </AspectRatio>

        <Stack style={{ flex: 1 }} gap='xs'>
          <Group justify='space-between' align='flex-start' wrap='nowrap'>
            <Stack gap={4}>
              <Group gap='sm' align='center'>
                <Title order={2}>
                  {summary.name ?? 'All Locations Bundle'}
                </Title>
                <Badge
                  variant='light'
                  style={{
                    backgroundColor: `${COLORS.LightGreen}22`,
                    color: COLORS.LightGreen,
                  }}
                >
                  All Locations
                </Badge>
                {summary.autoRenew && (
                  <Badge color='teal' variant='light'>
                    Auto-Renew
                  </Badge>
                )}
              </Group>
            </Stack>

            {/* Desktop "View Details" button */}
            <Button
              variant='outline'
              size='sm'
              visibleFrom='sm'
              onClick={() =>
                navigate(`/dashboard/myAds/bundle/${advertisementId}`)
              }
            >
              View Details
            </Button>
          </Group>

          {/* Stats row */}
          <Group gap='lg' wrap='wrap'>
            <Text
              size='sm'
              fw={500}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FontAwesomeIcon
                icon={faLocationDot}
                style={{ color: COLORS.HiddenGemzRed, width: 14 }}
              />
              {summary.activeLocationCount} active{' '}
              {summary.activeLocationCount === 1 ? 'location' : 'locations'}
            </Text>
          </Group>

          {/* Date / duration */}
          {dateRange && (
            <Text
              size='sm'
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                style={{ color: '#666', width: 14 }}
              />
              {dateRange}
            </Text>
          )}

          {/* Approval summary */}
          {approvalParts.length > 0 && (
            <Text size='sm' c='dimmed'>
              {approvalParts.join(', ')}
            </Text>
          )}

          {/* Mobile "View Details" button */}
          <Group hiddenFrom='sm' mt='sm' justify='flex-end'>
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                navigate(`/dashboard/myAds/bundle/${advertisementId}`)
              }
            >
              View Details
            </Button>
          </Group>
        </Stack>
      </Group>
    </Paper>
  )
}

export default AllLocationsBundleRow
